import React, { Component } from 'react';
import { Collapse, Navbar, NavbarBrand, NavbarToggler, NavItem, NavLink } from 'reactstrap';
import { Link } from 'react-router-dom';
import LoginMenu from './auth/LoginMenu';
import authService from '../services/authService';
import './NavMenu.css';

export class NavMenu extends Component {
  static displayName = NavMenu.name;

  constructor (props) {
    super(props);

    this.toggleNavbar = this.toggleNavbar.bind(this);
    this.state = {
      collapsed: true,
      isAuthenticated: false,
      roles: [],
      currentPath: window.location.pathname
    };
  }

  // Helper to check if current page is a public page (no auth required)
  isPublicPage() {
    const path = window.location.pathname || '';
    const publicPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/confirm-email', '/app-info', '/docs', '/pricing', '/'];
    return publicPaths.some(p => path === p || path.startsWith(p + '/'));
  }

  // Force re-render when auth state might have changed
  forceUpdate() {
    this.forceUpdate();
  }

  componentDidMount() {
    this.checkAuthStatus();
    // Always poll to keep auth state up to date, regardless of page type
    // This ensures header shows correct state even when navigating between public/private pages
    this.authInterval = setInterval(this.checkAuthStatus.bind(this), 1000); // Check every second
    // Prime roles if authenticated
    if (authService.isAuthenticated()) {
      this.updateRoles();
    }
    
    // Listen for authentication state changes (e.g., after login)
    this.handleAuthChange = () => {
      // Small delay to ensure localStorage is updated
      setTimeout(() => this.checkAuthStatus(), 100);
    };
    window.addEventListener('authStateChanged', this.handleAuthChange);
    
    // Also listen for storage changes (localStorage updates)
    this.handleStorageChange = (e) => {
      // Check if user was added to localStorage (login) or removed (logout)
      if (e.key === 'user' || e.key === null) {
        setTimeout(() => this.checkAuthStatus(), 100);
      }
    };
    window.addEventListener('storage', this.handleStorageChange);
    
    // Check auth status when window regains focus (user returns to tab/window)
    this.handleFocus = () => {
      this.checkAuthStatus();
    };
    window.addEventListener('focus', this.handleFocus);
    
    // Force a refresh after page loads to ensure header is correct
    // This handles cases where auth state is set but component hasn't updated yet
    // Multiple timeouts to catch different initialization scenarios
    setTimeout(() => {
      this.checkAuthStatus();
    }, 100);
    setTimeout(() => {
      this.checkAuthStatus();
    }, 500);
    setTimeout(() => {
      this.checkAuthStatus();
    }, 1000);
  }

  componentDidUpdate(prevProps, prevState) {
    // Check auth status when route changes (e.g., navigating to dashboard after login)
    const currentPath = window.location.pathname;
    if (prevState.currentPath !== currentPath) {
      this.setState({ currentPath });
      // Immediate check, then another check after a short delay to catch async updates
      this.checkAuthStatus();
      setTimeout(() => this.checkAuthStatus(), 200);
    }
    
    // Also check if auth state might have changed (user stored in localStorage)
    // This handles the case where login happens and user is stored but event hasn't fired yet
    if (!this.state.isAuthenticated) {
      const isAuth = authService.isAuthenticated();
      if (isAuth !== this.state.isAuthenticated) {
        this.checkAuthStatus();
      }
    }
  }

  componentWillUnmount() {
    if (this.authInterval) {
      clearInterval(this.authInterval);
      this.authInterval = null;
    }
    if (this.handleAuthChange) {
      window.removeEventListener('authStateChanged', this.handleAuthChange);
    }
    if (this.handleStorageChange) {
      window.removeEventListener('storage', this.handleStorageChange);
    }
    if (this.handleFocus) {
      window.removeEventListener('focus', this.handleFocus);
    }
  }

  checkAuthStatus() {
    // Always check actual auth status, even on public pages
    // Public pages don't require auth, but we should still show if user is logged in
    const isAuthenticated = authService.isAuthenticated();
    // Only update state if it actually changed to avoid infinite loops
    if (isAuthenticated !== this.state.isAuthenticated) {
      this.setState({ isAuthenticated });
      if (isAuthenticated) {
        this.updateRoles();
      } else {
        this.setState({ roles: [] });
        // Stop polling if user becomes unauthenticated
        if (this.authInterval) {
          clearInterval(this.authInterval);
          this.authInterval = null;
        }
      }
    } else if (isAuthenticated && this.state.roles.length === 0) {
      // If authenticated but roles not loaded yet, load them
      this.updateRoles();
    }
  }

  async updateRoles() {
    // Always fetch roles if authenticated, regardless of page type
    // This ensures header shows correct role-based links even on public pages
    
    try {
      if (!authService.isAuthenticated()) { 
        this.setState({ roles: [] }); 
        return; 
      }
      const info = await authService.getUserInfo();
      const roles = (info && info.roles) ? info.roles.map(r => String(r).toLowerCase()) : [];
      this.setState({ roles });
    } catch {
      // ignore role fetch errors - but stop polling if we get repeated errors
      // This prevents infinite loops after logout
      if (this.authInterval) {
        clearInterval(this.authInterval);
        this.authInterval = null;
      }
    }
  }

  toggleNavbar () {
    this.setState({
      collapsed: !this.state.collapsed
    });
  }

  render() {
    // Always check actual auth status in render to ensure UI reflects current state
    // This ensures header updates even when clicking tabs within the same page
    // Check auth regardless of page type - public pages can still show logged-in state
    const actualIsAuthenticated = authService.isAuthenticated();
    
    // If auth state doesn't match, schedule an update (but don't do it in render)
    if (actualIsAuthenticated !== this.state.isAuthenticated) {
      // Schedule update outside of render to avoid infinite loops
      if (!this._authCheckScheduled) {
        this._authCheckScheduled = true;
        // Use setTimeout to schedule update after render completes
        setTimeout(() => {
          this._authCheckScheduled = false;
          this.checkAuthStatus();
        }, 0);
      }
    }
    
    // Use actual auth status for rendering, not stale state
    // This ensures UI is always correct even if state hasn't updated yet
    const isAuthenticated = actualIsAuthenticated;
    const user = authService.getCurrentUser();
    const roles = (this.state.roles && this.state.roles.length > 0)
      ? this.state.roles
      : ((user && user.roles) ? user.roles.map(r => r.toLowerCase()) : []);

    const hasUnsavedAi = () => {
      try { return sessionStorage.getItem('ai_unsaved') === '1'; } catch { return false; }
    };
    const leavingAi = (to) => {
      try { const path = String(window.location.pathname || ''); return path.startsWith('/ai') && to !== '/ai'; } catch { return false; }
    };
    const requestNav = (e, to) => {
      if (leavingAi(to) && hasUnsavedAi()) {
        e.preventDefault();
        try { sessionStorage.setItem('ai_nav_confirm_to', to); } catch {}
        // trigger custom modal via event
        const ev = new CustomEvent('ai:confirm-leave');
        window.dispatchEvent(ev);
      }
    };

    return (
      <header>
        <Navbar className="navbar-expand-sm navbar-toggleable-sm ng-white border-bottom box-shadow mb-3" container light>
          <NavbarBrand tag={Link} to="/" style={{ display: 'flex', alignItems: 'center' }} onClick={(e) => requestNav(e, '/') }>
            <img src="/logo.png" alt="PocketPE Logo" style={{ height: '45px', width: 'auto' }} />
          </NavbarBrand>
          <NavbarToggler onClick={this.toggleNavbar} className="mr-2" />
          <Collapse className="d-sm-inline-flex flex-sm-row-reverse" isOpen={!this.state.collapsed} navbar>
            <ul className="navbar-nav flex-grow">
              {!isAuthenticated && (
                <>
                  <NavItem>
                    <NavLink tag={Link} className="text-dark" to="/app-info" onClick={(e) => requestNav(e, '/app-info') }>App Info</NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink tag={Link} className="text-dark" to="/docs" onClick={(e) => requestNav(e, '/docs') }>Docs</NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink tag={Link} className="text-dark" to="/register" onClick={(e) => requestNav(e, '/register') }>Register</NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink tag={Link} className="text-dark" to="/login" onClick={(e) => requestNav(e, '/login') }>Login</NavLink>
                  </NavItem>
                </>
              )}
              
              {isAuthenticated && (
                <>
                  <NavItem>
                    <NavLink tag={Link} className="text-dark" to="/ai">Chat</NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink tag={Link} className="text-dark" to="/baiql" onClick={(e) => requestNav(e, '/baiql') }>BAIQL</NavLink>
                  </NavItem>
                  {(roles.includes('teamadmin') || roles.includes('admin')) && (
                    <NavItem>
                      <NavLink tag={Link} className="text-dark" to="/teams" onClick={(e) => requestNav(e, '/teams') }>Teams</NavLink>
                    </NavItem>
                  )}
                  {(roles.includes('enterpriseadmin') || roles.includes('admin')) && (
                    <NavItem>
                      <NavLink tag={Link} className="text-dark" to="/enterprise" onClick={(e) => requestNav(e, '/enterprise') }>Enterprise</NavLink>
                    </NavItem>
                  )}
                  <NavItem>
                    <NavLink tag={Link} className="text-dark" to="/app-info" onClick={(e) => requestNav(e, '/app-info') }>App Info</NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink tag={Link} className="text-dark" to="/docs" onClick={(e) => requestNav(e, '/docs') }>Docs</NavLink>
                  </NavItem>
                  {roles.includes('admin') && (
                    <NavItem>
                      <NavLink tag={Link} className="text-dark" to="/admin" onClick={(e) => requestNav(e, '/admin') }>Admin</NavLink>
                    </NavItem>
                  )}
                </>
              )}
              
              <LoginMenu />
            </ul>
          </Collapse>
        </Navbar>
      </header>
    );
  }
}
